import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API, formatApiErrorDetail } from "@/App";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ArrowLeft, CalendarPlus, Loader2, Plus, Minus, Users, ChevronLeft, ChevronRight } from "lucide-react";
import { format, addDays, startOfWeek, addWeeks } from "date-fns";

const AddEvent = () => {
  const { getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [positions, setPositions] = useState([]);
  const [weekDays, setWeekDays] = useState([]);
  const [weekOffset, setWeekOffset] = useState(0);
  
  const [formData, setFormData] = useState({
    event_name: "",
    start_time: "",
    end_time: "",
    days: [],
    position_slots: [],
  });

  useEffect(() => {
    fetchPositions();
  }, []);

  useEffect(() => {
    generateWeekDays(weekOffset);
  }, [weekOffset]);

  const fetchPositions = async () => {
    try {
      const response = await axios.get(`${API}/positions`, {
        headers: getAuthHeaders(),
        
      });
      setPositions(response.data);
      // Initialize position_slots with all positions at 0
      setFormData(prev => ({
        ...prev,
        position_slots: response.data.map(p => ({ position: p.name, count: 0 }))
      }));
    } catch (error) {
      console.error("Error fetching positions:", error);
    }
  };

  const generateWeekDays = (offset = 0) => {
    const today = new Date();
    const base = addWeeks(today, offset);
    const start = startOfWeek(base, { weekStartsOn: 0 });
    const days = [];
    for (let i = 0; i < 7; i++) {
      const day = addDays(start, i);
      days.push({
        date: format(day, "yyyy-MM-dd"),
        dayName: format(day, "EEE"),
        dayNumber: format(day, "d"),
        fullDate: format(day, "MMM d"),
      });
    }
    setWeekDays(days);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleDayToggle = (date) => {
    setFormData((prev) => {
      const currentDays = prev.days;
      if (currentDays.includes(date)) {
        return { ...prev, days: currentDays.filter((d) => d !== date) };
      } else {
        return { ...prev, days: [...currentDays, date] };
      }
    });
  };

  const handlePositionCountChange = (positionName, delta) => {
    setFormData((prev) => ({
      ...prev,
      position_slots: prev.position_slots.map(slot => {
        if (slot.position === positionName) {
          return { ...slot, count: Math.max(0, slot.count + delta) };
        }
        return slot;
      })
    }));
  };

  const setPositionCount = (positionName, count) => {
    setFormData((prev) => ({
      ...prev,
      position_slots: prev.position_slots.map(slot => {
        if (slot.position === positionName) {
          return { ...slot, count: Math.max(0, parseInt(count) || 0) };
        }
        return slot;
      })
    }));
  };

  const getTotalSlots = () => {
    return formData.position_slots.reduce((sum, slot) => sum + slot.count, 0);
  };

  const getActivePositions = () => {
    return formData.position_slots.filter(slot => slot.count > 0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.days.length === 0) {
      toast.error("Please select at least one day");
      return;
    }
    
    const activePositions = getActivePositions();
    if (activePositions.length === 0) {
      toast.error("Please add at least one position slot");
      return;
    }

    setLoading(true);

    try {
      const payload = {
        event_name: formData.event_name,
        start_time: formData.start_time,
        end_time: formData.end_time,
        days: formData.days,
        position_slots: activePositions,
      };
      
      const response = await axios.post(`${API}/events`, payload, {
        headers: getAuthHeaders(),
        
      });
      
      toast.success(response.data.message);
      navigate("/company-schedule");
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || "Failed to create event");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="add-event-page">
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate(-1)}
          className="text-[#666666] hover:text-[#1A1A1A]"
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="text-3xl font-bold font-heading text-[#1A1A1A]">Add Event</h1>
          <p className="text-[#666666]">Create an event with position-specific staffing</p>
        </div>
      </div>

      <Card className="border border-[#E5E5E5] shadow-none max-w-2xl">
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center gap-2 text-[#1A1A1A]">
            <CalendarPlus className="w-5 h-5 text-[#6A1E2E]" />
            Event Details
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6" data-testid="add-event-form">
            {/* Event Name */}
            <div className="space-y-2">
              <Label htmlFor="event_name">Event Name</Label>
              <Input
                id="event_name"
                name="event_name"
                value={formData.event_name}
                onChange={handleInputChange}
                placeholder="e.g., Morning Tour, Night Security"
                required
                className="border-[#E5E5E5] focus:border-[#6A1E2E]"
                data-testid="input-event-name"
              />
            </div>

            {/* Time Row - Military Time */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="start_time">Start Time (Military)</Label>
                <Input
                  id="start_time"
                  name="start_time"
                  type="time"
                  value={formData.start_time}
                  onChange={handleInputChange}
                  required
                  className="border-[#E5E5E5] focus:border-[#6A1E2E] font-mono"
                  data-testid="input-start-time"
                />
                {formData.start_time && (
                  <p className="text-xs text-[#999999]">{formData.start_time} hrs</p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="end_time">End Time (Military)</Label>
                <Input
                  id="end_time"
                  name="end_time"
                  type="time"
                  value={formData.end_time}
                  onChange={handleInputChange}
                  required
                  className="border-[#E5E5E5] focus:border-[#6A1E2E] font-mono"
                  data-testid="input-end-time"
                />
                {formData.end_time && (
                  <p className="text-xs text-[#999999]">{formData.end_time} hrs</p>
                )}
              </div>
            </div>

            {/* Position Slots */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="flex items-center gap-2">
                  <Users className="w-4 h-4 text-[#6A1E2E]" />
                  Workers Needed by Position
                </Label>
                {getTotalSlots() > 0 && (
                  <span className="text-sm font-semibold text-[#6A1E2E]">
                    Total: {getTotalSlots()} workers
                  </span>
                )}
              </div>
              
              <div className="border border-[#E5E5E5] rounded-sm divide-y divide-[#E5E5E5]">
                {formData.position_slots.map((slot, idx) => (
                  <div
                    key={slot.position}
                    className={`flex items-center justify-between p-3 ${
                      slot.count > 0 ? "bg-[#F0EAEB]" : "bg-white"
                    }`}
                  >
                    <span className={`font-medium ${slot.count > 0 ? "text-[#6A1E2E]" : "text-[#666666]"}`}>
                      {slot.position}
                    </span>
                    <div className="flex items-center gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        className="h-8 w-8 border-[#E5E5E5]"
                        onClick={() => handlePositionCountChange(slot.position, -1)}
                        disabled={slot.count === 0}
                      >
                        <Minus className="w-4 h-4" />
                      </Button>
                      <Input
                        type="number"
                        min="0"
                        max="50"
                        value={slot.count}
                        onChange={(e) => setPositionCount(slot.position, e.target.value)}
                        className="w-16 text-center border-[#E5E5E5] font-mono"
                        data-testid={`position-count-${slot.position.toLowerCase().replace(/\s+/g, "-")}`}
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        className="h-8 w-8 border-[#E5E5E5]"
                        onClick={() => handlePositionCountChange(slot.position, 1)}
                      >
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
                {formData.position_slots.length === 0 && (
                  <div className="p-4 text-center text-[#999999] text-sm">
                    No positions available. Add positions in Settings first.
                  </div>
                )}
              </div>
              
              {/* Active positions summary */}
              {getActivePositions().length > 0 && (
                <div className="text-sm text-[#666666] p-2 bg-[#FAFAFA] rounded-sm">
                  <span className="font-medium">Summary: </span>
                  {getActivePositions().map((slot, idx) => (
                    <span key={slot.position}>
                      {slot.count} {slot.position}
                      {idx < getActivePositions().length - 1 ? ", " : ""}
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Day Selection */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label>Select Days</Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const allDates = weekDays.map(d => d.date);
                    const allSelected = allDates.every(d => formData.days.includes(d));
                    setFormData(prev => ({ ...prev, days: allSelected ? [] : [...new Set([...prev.days, ...allDates])] }));
                  }}
                  className="border-[#E5E5E5] text-xs h-7"
                  data-testid="select-all-days"
                >
                  {weekDays.length > 0 && weekDays.every(d => formData.days.includes(d.date)) ? "Deselect All" : "Select All"}
                </Button>
              </div>
              <div className="flex items-center justify-between">
                <Button type="button" variant="ghost" size="icon" onClick={() => setWeekOffset(prev => prev - 1)} className="h-8 w-8" data-testid="prev-week">
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <p className="text-sm font-medium text-[#666666]">
                  {weekOffset === 0 ? "This Week" : weekOffset > 0 ? `${weekOffset} week${weekOffset > 1 ? "s" : ""} ahead` : `${Math.abs(weekOffset)} week${Math.abs(weekOffset) > 1 ? "s" : ""} ago`}: {weekDays.length > 0 && `${weekDays[0].fullDate} - ${weekDays[6].fullDate}`}
                </p>
                <Button type="button" variant="ghost" size="icon" onClick={() => setWeekOffset(prev => prev + 1)} className="h-8 w-8" data-testid="next-week">
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
              <div className="grid grid-cols-7 gap-2">
                {weekDays.map((day) => (
                  <button
                    key={day.date}
                    type="button"
                    onClick={() => handleDayToggle(day.date)}
                    className={`flex flex-col items-center p-3 rounded-sm border transition-colors ${
                      formData.days.includes(day.date)
                        ? "bg-[#6A1E2E] text-white border-[#6A1E2E]"
                        : "bg-white text-[#1A1A1A] border-[#E5E5E5] hover:border-[#6A1E2E]"
                    }`}
                    data-testid={`day-${day.dayName.toLowerCase()}`}
                  >
                    <span className="text-xs font-semibold uppercase">{day.dayName}</span>
                    <span className="text-lg font-bold">{day.dayNumber}</span>
                  </button>
                ))}
              </div>
              {formData.days.length > 0 && (
                <p className="text-sm text-[#6A1E2E]">
                  {formData.days.length} day(s) selected × {getTotalSlots()} workers = {formData.days.length * getTotalSlots()} total shifts
                </p>
              )}
            </div>

            {/* Submit */}
            <div className="flex gap-4 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate(-1)}
                className="border-[#E5E5E5]"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={loading}
                className="bg-[#6A1E2E] hover:bg-[#521422] text-white"
                data-testid="submit-event"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Creating...
                  </>
                ) : (
                  "Create Event"
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default AddEvent;
